import {funcOrValOr, getAgo, MS_DAY, msToDays, msToInputDate, pickKeys} from "./CommonJS";
import {ApiParams, ApiParamsMap} from "../app/ApiParamsTypes";

export interface QueryParams<K extends keyof ApiParamsMap & string> {
    site?: string;
    method?: string;
    api: K;
    params?: ApiParams<K> | (() => ApiParams<K>);
    onJson?: (value?: unknown, params?: Record<string, any>) => void;
    alt?: unknown;
    contentType?: string;
    headers?: HeadersInit | (() => Promise<HeadersInit>) | Promise<HeadersInit>;
    onRsp?: (rsp: Response, params?: ApiParams<K> | (() => ApiParams<K>)) => boolean | void;
    fetchProps?: RequestInit;
}

export interface QueryReturn {
    cancel: () => void,
    created: Date,
    api: string,
    isActive: boolean,
    promise?
}

export function query<K extends keyof ApiParamsMap & string>({site = "", method, api, params, onJson, alt, contentType, headers, onRsp, fetchProps}: QueryParams<K>): QueryReturn {
    if (api?.[0] !== "/" && !api.startsWith("http")) api = ("/" + api as any);
    const controller = new AbortController(), {signal} = controller;
    const hq: QueryReturn = {
        api,
        isActive: true,
        created: new Date(),
        cancel: () => {
            if (!hq.isActive) return;
            hq.isActive = false;
            controller.abort(`Fetch aborted after ${getAgo(hq.created)}: ${api}`);
        }
    };

    function finish(value: unknown) {
        if (hq.isActive) {                  // if cancelled, don't issue callback.
            hq.isActive = false;
            onJson?.(value, params);        // allow original params to be referenced in the callback.
        }
    }

    (async () => {
        const hdrEx = {
            "Cache-Control": "max-age=3600",
            ...(contentType === "" ? {} : {"content-type": contentType || "application/json"}),
            "Access-Control-Request-Method": "POST",
            ...await funcOrValOr(headers)
        };

        hq.promise = fetch(site + api, {
            method: method || "POST",
            signal,
            headers: hdrEx,
            ...method !== "GET" && {body: JSON.stringify(params)},
            ...fetchProps
        }).then(async rsp => {
            if (alt && rsp.status > 204 && import.meta.env.VITE_ENV?.toLowerCase().startsWith("dev"))
                return finish(funcOrValOr(alt, {api, ...params}));
            if (rsp.status > 204) {
                let text = await rsp.text(), json;
                if (text?.[0] === "{") json = JSON.parse(text);
                return finish({
                    error: rsp.status === 416? "Error: there is no role assigned to your account." :
                        `API "${api}" failed!\n\nError: ${rsp.status}${rsp.statusText || json?.title? ` - ${rsp.statusText || json?.title}` : ""}`,
                    errText: rsp.status === 416? "Please ask your Remedio administrator to assign a role to your account. This can be done by logging into the system, then pressing \"Permissions\" on the left menu." :
                        json?.msg || json?.error || (
                            json?.errors? JSON.stringify(json.errors, null, 2).slice(2, -2) :
                                text?.[0] !== "<"? text : "")
                });
            }
            if (onRsp?.(rsp, params)) return;
            rsp.json().then(finish).catch(error => { if (!signal.aborted) throw (error); });
        }).catch(error => {
            if (alt && error.name === "TypeError")                      // allow development not to have valid URLs.
                return finish(funcOrValOr(alt, {api, ...params}));
            console.log(error);
            finish({error});
        });
    })();

    return hq;
}

export function downloadUri({blob, content, uri, filename}) {
    const blobId = (blob || content) && URL.createObjectURL(new Blob([blob ?? content], {type: "text/plain"}));
    const a = document.createElement("a");
    a.href = blobId ?? uri;
    a.download = filename;
    document.body.appendChild(a); // Required for this to work in FireFox
    a.click();
    a.remove();
    blobId && URL.revokeObjectURL(blobId);
}

export function selectFile(onSelect, acceptType = "*.*") {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = acceptType;
    input.onchange = (e: InputEvent & {target: HTMLInputElement}) => onSelect?.(e.target.files?.[0], acceptType);
    document.body.appendChild(input);       // Required for this to work in FireFox
    input.click();
    document.body.removeChild(input);
}

export function readFile(file, onRead) {
    const reader = new FileReader();
    reader.onload = event => onRead(event.target?.result);
    reader.readAsText(file);
    //reader.readAsBinaryString(file)
}

export const timePickerFrmt = (date?: string | Date | number) => new Date(date || Date.now()).toISOString().slice(0, 10);


export function copyToClipboard(text: string) {
    // Modern API (preferred)
    if (navigator.clipboard && window.isSecureContext)
        return navigator.clipboard.writeText(text);

    // Fallback for older browsers
    return new Promise((resolve, reject) => {
        try {
            const textarea = document.createElement("textarea");
            textarea.value = text;

            // Prevent scrolling to bottom on iOS
            textarea.style.position = "fixed";
            textarea.style.top = "-9999px";
            textarea.style.left = "-9999px";

            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();

            const successful = document.execCommand("copy");
            document.body.removeChild(textarea);

            successful ? resolve(true) : reject(false);
        } catch (err) {
            reject(err);
        }
    });
}


let cacheRemPixels;

export function remPixels() {
    if (cacheRemPixels) return cacheRemPixels;
    const calcRemPixels = () => (cacheRemPixels = parseFloat(getComputedStyle(document.documentElement).fontSize));
    window.addEventListener("resize", calcRemPixels);
    return calcRemPixels();
}

export function clsJoin(...args) {
    return args.filter(a => a).join(" ");
}

export function clsNms(...args) {
    const arr = args.filter(a => a);
    return arr.length? {className: arr.join(" ")} : {};
}

const nav = window.navigator;
export const locale = (nav.languages && nav.languages[0]) || nav.language;

//const dateTimeLocaleOptions = {dateStyle: "short", timeStyle: "short"};
const dateTimeLocaleOptions = {year: "numeric", month: "2-digit", day: "numeric", hour: "numeric", minute: "2-digit"};

function procDate(dt, flags: {never?: boolean, invalid?: boolean} = {}) {
    const {never, invalid} = flags;
    dt = dt?.cellData ?? dt;
    if (!dt) return "";
    if (typeof dt === "string" || typeof dt === "number") dt = new Date(dt);
    if (isNaN(dt)) return invalid || "(invalid)";
    if (dt < 0) return never || "(never)";
    return dt;
}

export function dispDateTime(dt, noTime = false) {
    dt = procDate(dt);
    if (typeof dt === "string") return dt;
    return dt.toLocaleString("ja-JP", noTime ? pickKeys(dateTimeLocaleOptions, ["year", "month", "day"]) : dateTimeLocaleOptions).split(",").join("");
}

export function fmtDateShortMonth(dt, flags = {}) {
    dt = procDate(dt, flags);
    if (typeof dt === "string") return dt;
    return dt.toLocaleString("en-US", {month: "short", day: "numeric", year: "numeric"});
}

export const commafyPluralCls = (n, text, className) => <><span className={className}>{(n*1).toLocaleString()}</span> {text}{(n*1).plural}</>;

export function emailToName(val) {
    const email = val?.cellData ?? val;
    return typeof email === "string" ? email.before("@").capitalize() : null;
}

export function msToExpDate(val) {
    const expDate = val?.cellData ?? val;
    return expDate? msToInputDate(expDate - MS_DAY) : null;
}

export function msToExpDays(val) {
    const expDate = val?.cellData ?? val;
    return msToDays(expDate);
}

export const LoadingAnim = ({className, classNameSize = "w-3 h-3", noSrvCache, error, reload}: any) => (
    <div data-test="loading" className={`animate-opacity flex flex-col justify-center items-center relative gap-2 ${className}`}>
        {!error?
            <div className={`animate-spin border-2 border-r-transparent rounded-full ${classNameSize}`}/>
            :
            <div className="flex flex-col items-center cursor-pointer" onClick={() => reload?.()} title="Reload...">
                <span className={`self-center text-sec text-[2rem]`}>⚠</span>
                <div className="text-xs text-sec whitespace-pre-line text-center">{error.error.toString?.() ?? "Error"}</div>
            </div>
        }
        {noSrvCache && <div data-test="no-server-cache-msg">Server is calculating results for this data set for the first time.</div>}
        {noSrvCache && <div>This might take several minutes...</div>}
    </div>
);

export const LoadingAnimLine = ({className = ""}) => (
    <div data-test="loading" className={`flex justify-center animate-opacity items-center gap-1 ${className || "[&>div]:w-2 [&>div]:h-2 [&>div]:bg-badge"}`}>
        <div className="animate-pulse"></div>
        <div className="animate-[pulse_2s_cubic-bezier(0.4,0,0.6,1)_0.3s_infinite]"></div>
        <div className="animate-[pulse_2s_cubic-bezier(0.4,0,0.6,1)_0.6s_infinite]"></div>
    </div>
);

const svgTags = {
    path:       dv => ({d: dv.path ?? dv}),
    polyline:   dv => ({points: dv.polyline}),
    polygon:    dv => ({points: dv.polygon}),
    circle:     dv => {const [cx, cy, r] = dv.circle; return {cx, cy, r};},
    line:       dv => {const [x1, y1, x2, y2] = dv.line; return {x1, y1, x2, y2};},
    rect:       dv => {const [x, y, width, height, rx, ry] = dv.line; return {x, y, width, height, rx, ry};},
};

export const MkSvg = ({className = "h-4 w-4", d = "", pStrokes = undefined, style = undefined, viewBox = undefined, ...props}) => (
    <svg
        xmlns="http://www.w3.org/2000/svg" className={className}
        {...!props.fill && props.strokeWidth !== "0" && {fill: "none"}} viewBox={viewBox ?? "0 0 24 24"} stroke="currentColor" {...props}>
        {[].concat(d).map((dv, i) => {
            const Tag = dv.tag ?? Object.keys(svgTags).find(k => dv[k]) ?? "path";
            const eProps = {...svgTags[Tag](dv), ...typeof dv === "object" && dv as object, [Tag]: undefined, tag: undefined};
            return <Tag
                key={i}
                {...(pStrokes || {strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: props.strokeWidth ?? 2})}
                style={Array.isArray(style) ? style[i] : style}
                {...eProps ?? {d: dv}}/>;
        })}
    </svg>
);


export const allowDrag = obj => ({
    draggable: true,
    onDragStart: e => e.dataTransfer.setData("obj", JSON.stringify(obj))
});

export const allowDrop = (obj, onDrop) => ({
    onDragOver: e => e.preventDefault(),
    onDrop: e => {
        e.preventDefault();                             // prevent the browser default handling of the data (default is open as link on drop)
        onDrop(JSON.parse(e.dataTransfer.getData("obj")), obj);
    }
});

export const allowDragDrop = (obj, onDrop) => ({...allowDrag(obj), ...allowDrop(obj, onDrop)});


// returns true if keypress handled
//
export function onKeyHandler(e, keyHandlers) {
    keyHandlers = funcOrValOr(keyHandlers);
    const fn = keyHandlers[e.code] ?? keyHandlers[e.key];
    if (!fn) return;
    let unhandle;
    fn(e, () => unhandle = true);
    if (unhandle) return;
    e.stopPropagation();
    e.preventDefault();
    return true;
}

// Handle key presses.
// Usage: within element props, apply as `{...genOnKeys({Enter: doSave, p: (e, unhandle) => !e.altKey? unhandle() : onPaste()})}`
//
export function genOnKeys(keyHandlers) {
    return {onKeyDown: e => onKeyHandler(e, keyHandlers)};
}

export const decodeStr = (str) => {
    const doc = new DOMParser().parseFromString(str, "text/html");
    return doc.documentElement.textContent;
};

export function onTitle(title) {
    if (!(title ?? true)) return {};        // undefined or null?
    if (typeof title !== "function") return {title};
    return {onMouseEnter: e => e.target.title = title(e)};
}

export function tooltipIfNeeded({currentTarget}) {
    if (currentTarget.scrollWidth > currentTarget.clientWidth)
        currentTarget.title = currentTarget.innerText; // Set the tooltip to the full text content
}

export function titleOnHover() {
    return {
        onMouseEnter: ({target}) => {
            if (!target.title && target.scrollWidth > target.clientWidth && !target.childElementCount)
                target.title = target.innerText;
        }
    };
}

// Settings
export let classNameScrollbar = "", classNameScrollbarThin = "";

export const setClassNameScrollbar = (norm, thin) => {
    classNameScrollbar = norm;
    classNameScrollbarThin = thin;
};
